/**
 * AdminRbacAudit — Super-admin-only compliance view.
 * GET /api/superadmin/rbac-audit → static+live report of everyone with access to
 * the platform-wide admin surface, plus orphan detection for admins pointing to
 * deleted tenants.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Users, AlertTriangle, Route as RouteIcon, ArrowLeft, Loader2, CheckCircle2, Mail, Download } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const StatCard = ({ icon: Icon, label, value, accent = 'indigo', testId }) => (
  <div className={`rounded-lg border bg-white p-4`} data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-${accent}-500/10 text-${accent}-600`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  </div>
);

const AdminRbacAudit = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [bulkDialog, setBulkDialog] = useState(null); // { tenant_id, tenant_name }
  const [bulkTypes, setBulkTypes] = useState({ admin: true, teacher: true, student: false, parent: false });
  const [bulkOnlyInactive, setBulkOnlyInactive] = useState(false);
  const [bulkDryRun, setBulkDryRun] = useState(true);
  const [bulkResult, setBulkResult] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['rbac-audit'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/rbac-audit`, { headers: hdrs() });
      if (!r.ok) {
        let detail = '';
        try { detail = (await r.json())?.detail || ''; } catch (_e) { /* ignore */ }
        const err = new Error(detail || `Request failed (HTTP ${r.status})`);
        err.status = r.status;
        throw err;
      }
      return r.json();
    },
    retry: false,
  });

  const bulkMutation = useMutation({
    mutationFn: async ({ tenant_id, dry_run }) => {
      const user_types = Object.keys(bulkTypes).filter(k => bulkTypes[k]);
      if (user_types.length === 0) throw new Error('Pick at least one user type');
      const r = await fetch(`${API_URL}/api/superadmin/rbac-audit/bulk-reset-emails`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ tenant_id, user_types, only_inactive: bulkOnlyInactive, dry_run }),
      });
      const js = await r.json();
      if (!r.ok) throw new Error(js.detail || 'Bulk reset failed');
      return js;
    },
    onSuccess: (res) => {
      setBulkResult(res);
      if (res.dry_run) {
        toast.info(`Preview: ${res.total_recipients} recipient(s) would receive the email`);
      } else {
        toast.success(`Sent: ${res.sent} · skipped: ${res.skipped} · failed: ${res.failed}`);
        qc.invalidateQueries({ queryKey: ['rbac-audit'] });
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const promoteMutation = useMutation({
    mutationFn: async ({ admin_id, is_super_admin }) => {
      const r = await fetch(`${API_URL}/api/superadmin/admins/${admin_id}/promote`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ is_super_admin }),
      });
      const js = await r.json();
      if (!r.ok) throw new Error(js.detail || 'Promotion failed');
      return js;
    },
    onSuccess: (res) => {
      toast.success(res.message || 'Updated');
      qc.invalidateQueries({ queryKey: ['rbac-audit'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const openBulk = (row) => {
    setBulkResult(null);
    setBulkTypes({ admin: true, teacher: true, student: false, parent: false });
    setBulkOnlyInactive(false);
    setBulkDryRun(true);
    setBulkDialog({ tenant_id: row.tenant_id, tenant_name: row.tenant_name || row.tenant_id });
  };

  if (isLoading) {
    return (
      <DashboardLayout title="RBAC Audit">
        <div className="flex items-center justify-center py-20" data-testid="rbac-audit-loading">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    const status = error?.status;
    const isAuth = status === 401 || status === 403;
    return (
      <DashboardLayout title="RBAC Audit">
        <div className="max-w-xl mx-auto py-12" data-testid="rbac-audit-error">
          <Card className={isAuth ? 'border-amber-300' : 'border-rose-300'}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className={`h-5 w-5 ${isAuth ? 'text-amber-600' : 'text-rose-600'}`} />
                {isAuth ? 'Super-admin access required' : 'Failed to load RBAC audit'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {isAuth ? (
                <>
                  <p>
                    This page is only visible to <strong>super admins</strong>. Your current
                    account is signed in but does not have the <code>is_super_admin</code> flag.
                  </p>
                  <p className="text-xs text-slate-500">
                    Ask another super admin to grant you access, or sign out and log back in as a super-admin account.
                  </p>
                </>
              ) : (
                <p className="text-rose-700">
                  {error?.message || 'Unknown error'}
                  {status ? <span className="text-slate-400"> (HTTP {status})</span> : null}
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="rbac-audit-back-btn">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to Admin
                </Button>
                <Button
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  data-testid="rbac-audit-retry-btn"
                >
                  {isFetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="RBAC Audit">
      <div className="space-y-6" data-testid="rbac-audit-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Admin
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-600" /> RBAC Audit
              </h1>
              <p className="text-xs text-slate-500">Who can access which part of the platform.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/role-builder')}
              data-testid="open-role-builder-btn"
            >
              <Shield className="h-4 w-4 mr-1" /> Role Builder
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const r = await fetch(`${API_URL}/api/superadmin/audit-logs/export`, { headers: hdrs() });
                  if (!r.ok) throw new Error('Export failed');
                  const blob = await r.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = r.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'audit-logs.csv';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Audit log exported');
                } catch (e) {
                  toast.error(e.message || 'Export failed');
                }
              }}
              data-testid="audit-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" /> Export audit CSV
            </Button>
            {data.orphan_admin_count === 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> No orphaned admins
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={RouteIcon} label="Super-only routes" value={data.super_only_route_count} testId="stat-routes" />
          <StatCard icon={Shield} label="Super admins" value={data.platform_super_admin_count} accent="rose" testId="stat-super" />
          <StatCard icon={Users} label="Scoped admins" value={data.scoped_admin_count} accent="emerald" testId="stat-scoped" />
          <StatCard icon={AlertTriangle} label="Orphan admins" value={data.orphan_admin_count} accent="amber" testId="stat-orphan" />
        </div>

        {/* Platform admins */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Platform admins ({data.platform_admin_count})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Super admin?</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.platform_admins.map(a => (
                  <TableRow key={a.id} data-testid={`platform-admin-${a.id}`}>
                    <TableCell className="font-medium">{a.name || '—'}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{a.email}</TableCell>
                    <TableCell className="text-xs capitalize">{a.role}</TableCell>
                    <TableCell>
                      {a.is_super_admin
                        ? <Badge className="bg-rose-100 text-rose-700 border-rose-200">Super</Badge>
                        : <Badge variant="outline">No</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={promoteMutation.isPending}
                        onClick={() => {
                          const becomingSuper = !a.is_super_admin;
                          const msg = becomingSuper
                            ? `Grant SUPER ADMIN to ${a.email}? They will have unrestricted access to every tenant.`
                            : `Revoke super-admin from ${a.email}? They will lose platform-wide access.`;
                          if (!window.confirm(msg)) return;
                          promoteMutation.mutate({ admin_id: a.id, is_super_admin: becomingSuper });
                        }}
                        data-testid={`promote-btn-${a.id}`}
                      >
                        {a.is_super_admin ? 'Demote' : 'Promote to super'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Tenant-scoped admins */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Tenant-scoped admins ({data.scoped_admin_count})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.scoped_admins.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500">No tenant-scoped admins yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scoped_admins.map(a => (
                    <TableRow key={a.id} data-testid={`scoped-admin-${a.id}`}>
                      <TableCell className="font-medium">{a.tenant_name || a.tenant_id}</TableCell>
                      <TableCell>{a.name || '—'}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">{a.email}</TableCell>
                      <TableCell className="text-xs capitalize">{a.role}</TableCell>
                      <TableCell>
                        {a.is_active
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                          : <Badge variant="outline" className="text-slate-500">Disabled</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate(`/admin/schools/${a.tenant_id}`)}>
                          View school
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs ml-1 text-indigo-600"
                          onClick={() => openBulk(a)}
                          data-testid={`bulk-reset-btn-${a.tenant_id}`}
                        >
                          <Mail className="h-3.5 w-3.5 mr-1" /> Bulk reset
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Orphans */}
        {data.orphan_admin_count > 0 && (
          <Card className="border-amber-300">
            <CardHeader className="pb-3 bg-amber-50">
              <CardTitle className="text-base flex items-center gap-2 text-amber-800"><AlertTriangle className="h-4 w-4" /> Orphan admins ({data.orphan_admin_count})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Email</TableHead><TableHead>Tenant id</TableHead><TableHead>Role</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.orphan_admins.map(a => (
                    <TableRow key={a.id} data-testid={`orphan-admin-${a.id}`}>
                      <TableCell className="font-mono text-xs">{a.email}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{a.tenant_id}</TableCell>
                      <TableCell className="text-xs capitalize">{a.role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Super-only routes list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><RouteIcon className="h-4 w-4" /> Super-only routes ({data.super_only_route_count})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {data.super_only_routes.map(r => (
                <code key={r} className="text-[11px] bg-slate-100 border rounded px-2 py-0.5 font-mono">{r}</code>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bulk reset dialog */}
        <Dialog open={!!bulkDialog} onOpenChange={(o) => !o && setBulkDialog(null)}>
          <DialogContent className="sm:max-w-lg" data-testid="bulk-reset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Bulk send password-reset emails
              </DialogTitle>
              <DialogDescription>
                Sends the "Set your password" email to every selected user of{' '}
                <strong>{bulkDialog?.tenant_name}</strong>. Useful for onboarding a whole school at once.
                Each link is valid for 24 hours.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium mb-2">Who should receive the email?</div>
                <div className="grid grid-cols-2 gap-2">
                  {['admin', 'teacher', 'student', 'parent'].map(t => (
                    <label key={t} className="flex items-center gap-2 text-sm capitalize border rounded px-3 py-2 cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={bulkTypes[t]}
                        onCheckedChange={(v) => setBulkTypes(prev => ({ ...prev, [t]: !!v }))}
                        data-testid={`bulk-type-${t}`}
                      />
                      {t}s
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bulkOnlyInactive}
                  onCheckedChange={(v) => setBulkOnlyInactive(!!v)}
                  data-testid="bulk-only-inactive"
                />
                Only users who have <strong>never logged in</strong>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bulkDryRun}
                  onCheckedChange={(v) => setBulkDryRun(!!v)}
                  data-testid="bulk-dry-run"
                />
                Preview only (don't actually send)
              </div>

              {bulkResult && (
                <div className="rounded-lg border bg-slate-50 p-3 text-xs">
                  <div className="flex items-center gap-4 mb-2">
                    <span><strong>{bulkResult.total_recipients}</strong> recipient(s)</span>
                    {!bulkResult.dry_run && (
                      <>
                        <span className="text-emerald-700"><strong>{bulkResult.sent}</strong> sent</span>
                        {bulkResult.skipped > 0 && <span className="text-slate-500">{bulkResult.skipped} skipped</span>}
                        {bulkResult.failed > 0 && <span className="text-rose-600">{bulkResult.failed} failed</span>}
                      </>
                    )}
                  </div>
                  <div className="max-h-32 overflow-auto space-y-1">
                    {bulkResult.recipients.slice(0, 30).map(r => (
                      <div key={r.id} className="flex items-center justify-between font-mono">
                        <span>{r.email}</span>
                        <span className="text-slate-500 capitalize">{r.user_type}</span>
                      </div>
                    ))}
                    {bulkResult.recipients.length > 30 && (
                      <div className="text-slate-500">… and {bulkResult.recipients.length - 30} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialog(null)} data-testid="bulk-cancel">Close</Button>
              <Button
                disabled={bulkMutation.isPending}
                onClick={() => bulkMutation.mutate({ tenant_id: bulkDialog.tenant_id, dry_run: bulkDryRun })}
                data-testid="bulk-confirm"
              >
                {bulkMutation.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : (bulkDryRun ? 'Preview recipients' : 'Send emails')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminRbacAudit;
